import React, { useCallback, useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { useRealtimeTranscriber } from "../hooks/useRealtimeTranscriber";
import { getClassNames } from "../../helpers/classHelpers";
import style from "./TranscriptionButton.module.scss";
import ActiveMicIcon from "../active-mic-icon/ActiveMicIcon";
import { useAuth } from '../../contexts/auth/AuthContext';

export interface TranscriptionButtonHandle {
    stop: () => Promise<void>;
    start: () => Promise<void>;
    toggle: () => Promise<void>;
}

export interface TranscriptionButtonProps {
    /** Current message value */
    message: string;
    /** Callback to update the message */
    onMessageChange: (message: string) => void;
    /** Callback when recording state changes */
    onRunningChange?: (isRunning: boolean) => void;
    /** Whether the button should be disabled */
    isDisabled?: boolean;
    /** Language for transcription */
    language?: string;
    /** Label for start button */
    startTitle?: string;
    /** Label for stop button */
    stopTitle?: string;
    /** Ref to the textarea for auto-scrolling */
    textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export const TranscriptionButton = forwardRef<TranscriptionButtonHandle, TranscriptionButtonProps>(
    (
        {
            message,
            onMessageChange,
            onRunningChange,
            isDisabled = false,
            language = "en",
            startTitle = "Start voice input",
            stopTitle = "Stop voice input",
            textareaRef
        },
        ref
    ) => {
        const { getAccessToken } = useAuth();
        const messageRef = useRef(message);
        const scrollTimeoutRef = useRef<NodeJS.Timeout>();
        const insertedRef = useRef("");  // the tentative (partial) text currently sitting in the message
        const [isTransitioning, setIsTransitioning] = useState(false);

        // Keep message ref in sync
        useEffect(() => {
            messageRef.current = message;
        }, [message]);

        const { transcript, partial, isRunning, speechState, level, error, start, stop, clear } =
            useRealtimeTranscriber({
                language,
                getAccessToken,
            });

        const join = (base: string, text: string) => {
            const sep = base && !/[ \n]$/.test(base) ? " " : "";
            return { result: base + sep + text, inserted: sep + text };
        };


        // Notify parent when running state changes
        useEffect(() => {
            onRunningChange?.(isRunning);
        }, [isRunning, onRunningChange]);

        // Append new transcript text to message
        useEffect(() => {
            if (!transcript && !partial && !insertedRef.current) return;

            // 1. Peel off the previous partial, if it's still where we left it
            let base = messageRef.current;
            if (insertedRef.current && base.endsWith(insertedRef.current)) {
                base = base.slice(0, -insertedRef.current.length);
            }

            // 2. Commit any finished utterance permanently
            if (transcript) base = join(base, transcript).result;

            // 3. Lay down the new partial as tentative text
            let next = base;
            insertedRef.current = "";
            if (partial) {
                const { result, inserted } = join(base, partial);
                next = result;
                insertedRef.current = inserted;
            }

            if (next !== messageRef.current) onMessageChange(next);
            if (transcript) clear();

            // Scroll textarea to bottom
            if (textareaRef?.current) {
                scrollTimeoutRef && clearTimeout(scrollTimeoutRef.current);
                const elem = textareaRef.current!;
                scrollTimeoutRef.current = setTimeout(() => {
                    //elem.dispatchEvent(new Event('auto-resize'));
                    elem.focus();
                    setTimeout(() => elem.scrollTop = elem.scrollHeight, 50);
                }, 100);
            }

        }, [transcript, partial, onMessageChange, clear, textareaRef]);

        // Log errors
        useEffect(() => {
            if (error) {
                console.error("Transcription error:", error);
            }
        }, [error]);

        const handleClick = useCallback(async () => {
            if (isTransitioning) return;

            setIsTransitioning(true);
            try {
                if (isRunning) {
                    await stop();
                } else {
                    await start();
                }
            } finally {
                // Small delay before allowing another click
                setTimeout(() => setIsTransitioning(false), 300);
            }
        }, [isRunning, start, stop, isTransitioning]);

        const buttonDisabled = (isDisabled && !isRunning) || isTransitioning;

        // Expose start and stop method to parent
        useImperativeHandle(ref, () => ({
            stop,
            start,
            toggle: handleClick
        }));

        return (
            <div
                className={getClassNames([
                    style["transcription-button"],
                    isRunning && style["is-running"],
                    speechState === "started" && style["is-speaking"],
                ])}
                title={isRunning ? stopTitle : startTitle}
                onClick={buttonDisabled ? undefined : handleClick}
                style={{ cursor: buttonDisabled ? "not-allowed" : "pointer", opacity: buttonDisabled ? 0.5 : 1 }}
            >
                <ActiveMicIcon
                    active={isRunning}
                    preparing={isTransitioning}
                    audioLevel={level}
                />
            </div>
        );
    }
);